import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import queryString from 'query-string'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useFetchDocumentBodyAllowMultipart } from 'src/components/async/document/useDocument'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { isTrArtifactMime } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { LinkIcon } from 'src/components/Icons/FileType'

/**
 * Resolves tr-artifact/* evidence body to Artifact.id + Artifact.chat_id and
 * renders a link to Trustero Intelligence / Copilot chat with ?artifact= deep link.
 * Fetches the document body only after hover/focus prefetch or click/keyboard activation
 * so evidence grids do not issue one download+parse per row on mount.
 */
export const ArtifactEvidenceSourceLink = ({
  contentId,
  mime,
  style,
}: {
  contentId: string
  mime: string
  style?: React.CSSProperties
}): JSX.Element => {
  const params = useParams()
  const navigate = useNavigate()
  const fetchBody = useFetchDocumentBodyAllowMultipart(contentId, mime)
  const [tiHref, setTiHref] = useState<string | null>(null)

  const tiHrefRef = useRef<string | null>(null)
  const resolveSeqRef = useRef(0)
  const inflightRef = useRef<Promise<string | null> | null>(null)

  useEffect(() => {
    resolveSeqRef.current += 1
    setTiHref(null)
    tiHrefRef.current = null
    inflightRef.current = null
  }, [contentId, mime, params.pageContext])

  const resolveTiHref = useCallback(async (): Promise<string | null> => {
    if (!isTrArtifactMime(mime) || !contentId) {
      return null
    }
    if (tiHrefRef.current) {
      return tiHrefRef.current
    }
    if (inflightRef.current) {
      return inflightRef.current
    }

    const seq = resolveSeqRef.current

    const p = (async (): Promise<string | null> => {
      try {
        const body = await fetchBody()
        if (seq !== resolveSeqRef.current) {
          return null
        }
        if (!body || !(body instanceof Uint8Array)) {
          return null
        }
        const artifact = Artifact.deserializeBinary(body)
        const chatId = artifact.getChatId()
        const artifactId = artifact.getId()
        if (!chatId || !artifactId) {
          return null
        }
        return generatePermalink({
          pageContext: params.pageContext ?? '',
          modelType: MODEL_TYPE.CHAT,
          modelId: chatId,
          isInternalLink: true,
          includeSearchParams: true,
          search: queryString.parse(`?artifact=${artifactId}`, {
            arrayFormat: 'bracket',
          }),
        })
      } catch {
        return null
      }
    })()

    inflightRef.current = p

    try {
      const href = await p
      if (seq === resolveSeqRef.current && href) {
        tiHrefRef.current = href
        setTiHref(href)
      }
      return href
    } finally {
      if (inflightRef.current === p) {
        inflightRef.current = null
      }
    }
  }, [contentId, mime, fetchBody, params.pageContext])

  const baseStyle: React.CSSProperties = {
    textAlign: 'left',
    textDecoration: 'underline',
    display: 'inline-flex',
    gap: '0.5em',
    alignItems: 'center',
    cursor: 'pointer',
    ...style,
  }

  const prefetch = useCallback(() => {
    void resolveTiHref()
  }, [resolveTiHref])

  const activate = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation()
      void (async () => {
        const href = await resolveTiHref()
        if (href) {
          navigate(href)
        }
      })()
    },
    [navigate, resolveTiHref],
  )

  if (tiHref) {
    return (
      <Link to={tiHref} onClick={(e) => e.stopPropagation()} style={baseStyle}>
        Artifact
        <LinkIcon />
      </Link>
    )
  }

  return (
    <span
      role="link"
      tabIndex={0}
      onPointerEnter={prefetch}
      onFocus={prefetch}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          activate(e)
        }
      }}
      style={baseStyle}
    >
      Artifact
      <LinkIcon />
    </span>
  )
}
